import { Box, Collapse, Flex, Stack, Text } from '@chakra-ui/react'
import { faBooks } from '@fortawesome/pro-regular-svg-icons'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useState } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'

import { getTutorUrl } from '@/client/services/url'

import { NAVBAR_DESKTOP_WIDTH, TUTOR_NAVIGATION_ITEM_DESKTOP } from '@/components/Layout/constants'
import type { NavigationItemSlug } from '@/components/Layout/types'
import { useSharedNavigationItems } from '@/components/Layout/useSharedNavigationItems'
import ShwLink from '@/components/ShwLink'
import { TUTOR_RESOURCES } from '@/components/Tutor/constants'

export default function NavbarDesktop({
  activeNavItem,
  isUserInterestedTutor,
}: {
  activeNavItem?: NavigationItemSlug
  isUserInterestedTutor: boolean
}) {
  const sharedNavigationItems = useSharedNavigationItems()
  const [transitionDurationSeconds, setTransitionDurationSeconds] = useState(0)
  const [isTutorResourcesExpanded, setIsTutorResourcesExpanded] = useState(false)

  // Keeps the sidebar open through page refreshes while the
  // user's mouse is still over the sidebar hover region
  useEffect(() => {
    setTimeout(() => {
      setTransitionDurationSeconds(0.2)
    }, 250)
  }, [])

  return (
    <Flex
      direction="column"
      left={0}
      flexShrink={0}
      display={{ base: 'none', sm: 'flex' }}
      width="100px"
      zIndex={3}
    >
      <Flex className="group" direction="column" width="100px" flexGrow={1} minH={0}>
        <Flex
          direction="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          pl="14px"
          width={NAVBAR_DESKTOP_WIDTH}
          flexGrow={1}
          _groupHover={{
            width: '250px',
            bgColor: 'bg',
            borderRight: `1px solid ${chakraTokenToVar('border.muted')}`,
          }}
          transition={`all ${transitionDurationSeconds}s ease-in-out`}
          overflowX="hidden"
          overflowY="auto"
          css={{
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
          minH={0}
          gap="0"
        >
          <Flex
            mt="120px"
            direction="column"
            alignItems="flex-start"
            pl="8px"
            flexShrink={0}
            bgGradient={`linear(to bottom, ${chakraTokenToVar('bg')}, ${chakraTokenToVar('bg.subtle')}, ${chakraTokenToVar('bg')})`}
            boxShadow={`0px 6px 6px 0px ${chakraTokenToVar('border.muted')}`}
            width="64px"
            borderRadius="36px"
            py="16px"
            gap="16px"
            _groupHover={{
              width: '250px',
              bgGradient: 'unset',
              boxShadow: 'unset',
            }}
            transition={`all ${transitionDurationSeconds}s ease-in-out`}
          >
            {sharedNavigationItems.map(item => {
              const isActive = item.slug === activeNavItem

              return (
                <ShwLink key={item.href} href={item.href} _focus={{ outline: 'none' }}>
                  <Flex
                    direction="row"
                    alignItems="center"
                    width="48px"
                    height="48px"
                    pl="12px"
                    borderRadius="24px"
                    _groupHover={{ width: '202px' }}
                    cursor="pointer"
                    transition={`width ${transitionDurationSeconds}s ease-in-out, background-color ${transitionDurationSeconds > 0 ? 0.1 : 0}s ease-in-out`}
                    gap="24px"
                    overflow="hidden"
                    {...(isActive
                      ? { bgColor: 'primary.subtle' }
                      : { _hover: { bgColor: 'bg.subtle' } })}
                  >
                    <Box flexShrink={0}>
                      <FontAwesomeIcon
                        icon={isActive ? item.icons.active : item.icons.inactive}
                        width="24px"
                        color={
                          isActive ? chakraTokenToVar('primary.solid') : chakraTokenToVar('fg')
                        }
                      />
                    </Box>
                    <Text
                      fontWeight={isActive ? 600 : 500}
                      opacity={0}
                      _groupHover={{ opacity: 1 }}
                      transition={`opacity ${transitionDurationSeconds}s ease-in-out`}
                      flexShrink={0}
                      color={isActive ? 'primary.fg' : 'fg'}
                    >
                      {item.labels.desktop}
                    </Text>
                  </Flex>
                </ShwLink>
              )
            })}
          </Flex>
          <Box
            mt="16px"
            pl="8px"
            flexShrink={0}
            bgColor="bg"
            width="64px"
            borderRadius="36px"
            py="8px"
            boxShadow={`0px 6px 6px 0px ${chakraTokenToVar('border.muted')}`}
            overflow="hidden"
            _groupHover={{ boxShadow: 'unset', width: '228px', borderRadius: '16px' }}
            transition={`box-shadow ${transitionDurationSeconds}s ease-in-out, width ${transitionDurationSeconds}s ease-in-out, border-radius ${transitionDurationSeconds}s ease-in-out`}
          >
            <ShwLink href={getTutorUrl()} _focus={{ outline: 'none' }}>
              <Flex
                direction="row"
                alignItems="center"
                width="48px"
                height="48px"
                pl="12px"
                borderRadius="24px"
                _groupHover={{ width: '202px' }}
                cursor="pointer"
                transition={`width ${transitionDurationSeconds}s ease-in-out, background-color ${transitionDurationSeconds > 0 ? 0.1 : 0}s ease-in-out`}
                gap="24px"
                overflow="hidden"
                {...(activeNavItem === 'tutor'
                  ? { bgColor: 'primary.subtle' }
                  : { _hover: { bgColor: 'bg.subtle' } })}
              >
                <Box flexShrink={0}>
                  <FontAwesomeIcon
                    icon={
                      activeNavItem === 'tutor'
                        ? TUTOR_NAVIGATION_ITEM_DESKTOP.icons.active
                        : TUTOR_NAVIGATION_ITEM_DESKTOP.icons.inactive
                    }
                    width="24px"
                    color={
                      activeNavItem === 'tutor'
                        ? chakraTokenToVar('primary.fg')
                        : chakraTokenToVar('fg')
                    }
                  />
                </Box>
                <Text
                  fontWeight={activeNavItem === 'tutor' ? 600 : 500}
                  opacity={0}
                  _groupHover={{ opacity: 1 }}
                  transition={`opacity ${transitionDurationSeconds}s ease-in-out`}
                  flexShrink={0}
                  color={activeNavItem === 'tutor' ? 'primary.fg' : 'fg'}
                >
                  {isUserInterestedTutor
                    ? TUTOR_NAVIGATION_ITEM_DESKTOP.label
                    : TUTOR_NAVIGATION_ITEM_DESKTOP.labelBeforeTutorInterest}
                </Text>
              </Flex>
            </ShwLink>
            {isUserInterestedTutor ? (
              <Box display="none" _groupHover={{ display: 'block' }} minWidth="212px" mt="8px">
                <Flex
                  direction="row"
                  alignItems="center"
                  height="44px"
                  pl="12px"
                  borderRadius="24px"
                  cursor="pointer"
                  gap="16px"
                  overflow="hidden"
                  _hover={{ bgColor: 'bg.subtle' }}
                  onClick={() => setIsTutorResourcesExpanded(prev => !prev)}
                >
                  <Box flexShrink={0} width="24px" display="flex" justifyContent="center">
                    <FontAwesomeIcon icon={faBooks} width="20px" color={chakraTokenToVar('fg')} />
                  </Box>
                  <Flex alignItems="center" gap="8px" flex={1} pr="12px" overflow="hidden">
                    <Text fontWeight={500} fontSize="14px" flexShrink={0}>
                      Tutoring Resources
                    </Text>
                    <Box ml="auto" flexShrink={0}>
                      <FontAwesomeIcon
                        icon={isTutorResourcesExpanded ? faChevronUp : faChevronDown}
                        width="10px"
                        color={chakraTokenToVar('fg.subtle')}
                      />
                    </Box>
                  </Flex>
                </Flex>
                <Collapse in={isTutorResourcesExpanded} animateOpacity>
                  <Stack
                    spacing="0px"
                    pl="16px"
                    borderLeft="2px solid"
                    borderColor="border.muted"
                    ml="23px"
                    my="4px"
                  >
                    {TUTOR_RESOURCES.map(resource => (
                      <ShwLink
                        key={resource.title}
                        href={resource.href ?? ''}
                        _focus={{ outline: 'none' }}
                      >
                        <Text
                          py="6px"
                          pl="8px"
                          fontWeight={400}
                          fontSize="14px"
                          cursor="pointer"
                          _hover={{ bgColor: 'bg.subtle' }}
                          borderRadius="6px"
                        >
                          {resource.title}
                        </Text>
                      </ShwLink>
                    ))}
                  </Stack>
                </Collapse>
              </Box>
            ) : null}
          </Box>
        </Flex>
      </Flex>
    </Flex>
  )
}
